import { EditOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Switch,
  List,
  Menu,
  PageHeader,
  Row,
  Col,
  Breadcrumb,
  Tabs,
  Tag,
  Space,
  Typography,
  Empty,
  Dropdown,
  Modal,
  Checkbox,
  notification,
} from "antd";
import React, { useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useHistory, useParams } from "react-router";
import {
  createOrganizationApiPartner,
  fetchOrganizationDetails,
  updateOrganizationDetails,
} from "../../../../api/accounts/organization";
import { OrganizationDetailsCard } from "@secondcloset/web-components";
import moment from "moment";
import { Flex } from "../../../../css/styles";

import {
  FulfilledFromState,
  fallbackFulfilledFromMapping,
  getFulfilledFrom,
} from "../../../../lib/fulfillment/fulfilledFromMapping";
import { useFeatureFlagQuery } from "../../../../hooks/useFeatureFlagQuery";
import { ReturnPreferenceTable } from "../../../../components/ReturnPreferenceTable";
import Organization, { orgStatusColor } from "../../../../lib/organization";
import ShippingPreferences from "./ShippingPreferences";
import CarriersTable from "./CarriersTable";
import StoresTable from "./StoresTable";
import EventSubscriptionsTable from "./EventSubscriptionsTable";
import FreightcomCredentialsTable from "./FreightcomCredentialsTable";
import { fetchFreightcomCredentials } from "../../../../api/accounts/organization_freightcom";
import { Common } from "@secondcloset/types";
import { startCase } from "lodash-es";
import useURLState from "../../../../hooks/application/useURLStateV2";
import ApiPartnerKeys from "./InfoCard/Modal/ApiPartnerKeys";
import location from "@secondcloset/web-components/dist/lib/ca-us-provinces-states.json";
import { FulfillmentOrderIndexPage } from "../../../fulfillment/FulfillmentOrderIndexPage";
import OrganizationUsersIndexPage from "../OrganizationUsersIndexPage";
import ShipmentIndexPage from "../../../fulfillment/ShipmentIndexPage";
import ReturnsIndexPage from "../../../fulfillment/ReturnsIndexPage";
import FulfillmentProductIndexPage from "../../../fulfillment/FulfillmentProductIndexPage";
import FulfillmentASNIndexPage from "../../../fulfillment/FulfillmentASNIndexPage";
import InventoryTransferIndexPage from "../../../fulfillment/InventoryTransferIndexPage";
import { Link } from "react-router-dom";

const generalFulfillmentConfig = {
  inventory_source: { label: "Picked and packed by GoBolt", readOnly: true },
  auto_fulfill_orders: { label: "Automatically set orders as Ready to Fulfill" },
  auto_order_dispatch: { label: "Automatically create shipments" },
} as Record<string, { label: string; readOnly?: boolean }>;

const geoFilterConstraints = {
  ca: "Canada",
  us: "United States",
} as Record<string, string>;

const orgStatusTransition = {
  active: ["paused"],
  paused: ["active"],
  churned: [],
} as Record<Common.OrganizationStatus, Common.OrganizationStatus[]>;

export const OrganizationDetailsPage: React.FC = () => {
  const { organization_id: orgId } = useParams<{ organization_id: string }>();
  const query = { per_page: 10, organization_id: orgId };

  const history = useHistory();
  const [urlState, setUrlState] = useURLState({ tab: "general" });
  const showNewShipmentCreatePage = useFeatureFlagQuery("new-reverse-logistics-create-flow");
  const [isVisible, setIsVisible] = useState(false);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [isConcentChecked, setIsConcentChecked] = useState(false);
  const [status, setStatus] = useState<Common.Organization["status"]>();

  const { data: org, ...orgQuery } = useQuery(
    ["org", orgId],
    () => fetchOrganizationDetails(orgId, { with_facility_service_area: true }),
    {
      enabled: !!orgId,
      onSuccess: (data) => {
        const fulfilledFrom = getFulfilledFrom(data.default_fulfilled_from);
        setStatus(data.status);
        return data;
      },
    }
  );

  const { mutate: updateOrganization, ...mutateOrg } = useMutation(updateOrganizationDetails, {
    onSuccess: () => {
      orgQuery.refetch();
      notification.success({
        message: "Success",
        description: "Organization status updated successfully",
      });
    },
  });

  const freightcomQuery = useQuery(["organization_fc", orgId], () => fetchFreightcomCredentials(orgId));

  const { mutate: doCreateOrganizationApiPartner } = useMutation(createOrganizationApiPartner, {
    onSuccess(response) {
      if (!response.client_id || !response.client_secret) return;
      setIsVisible(true);
      setClientId(response.client_id);
      setClientSecret(response.client_secret);
    },
  });

  const resetUrlParams = () => {
    const params = new URLSearchParams(history.location.search);
    Array.from(params.keys()).forEach((key) => {
      if (key !== "tab") params.delete(key);
    });
    history.location.search = params.toString();
  };

  const handleTabChange = (tab: string) => {
    setUrlState({ tab });
    resetUrlParams();
  };

  const renderFulfillmentConfigs = () => {
    return (
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <GeneralFulfillmentConfig
            configs={
              org?.configurations_attributes.reduce((acc, cur) => ({ ...acc, [cur.key]: cur.value }), {}) as Record<
                Common.OrganizationConfigKey,
                unknown
              >
            }
          />
        </Col>
        <Col span={24}>
          <Geofilter geofilter={org?.geofilter_constraints || []} />
        </Col>
      </Row>
    );
  };

  const renderWarehouseConfigs = () => {
    return (
      <>
        {showNewShipmentCreatePage.data?.state && (
          <ReturnPreferenceTable
            organizationId={orgId}
            preferredReturnFacility={Organization.getPreferredReturnFacility(org)}
          />
        )}
      </>
    );
  };

  const renderCarrierConfigs = () => {
    return (
      <>
        <ShippingPreferences organizationId={orgId} />
        {Organization.isEndy(org) ? <CarriersTable query={{ ...query, include_defaults: false }} /> : null}
      </>
    );
  };

  const renderIntegrationConfigs = () => {
    return (
      <>
        <StoresTable query={query} />
        <EventSubscriptionsTable query={query} />
        <FreightcomCredentialsTable
          organizationID={orgId}
          freightcomCredentials={freightcomQuery.data || []}
          loading={freightcomQuery.isLoading}
          refetchFreightcomCredentials={freightcomQuery.refetch}
        />
      </>
    );
  };

  const renderTabs = (tab: string) => {
    switch (tab) {
      case "users":
        return <OrganizationUsersIndexPage />;
      case "orders":
        return <FulfillmentOrderIndexPage />;
      case "shipments":
        return <ShipmentIndexPage />;
      case "returns":
        return <ReturnsIndexPage />;
      case "products":
        return <FulfillmentProductIndexPage />;
      case "ASNs":
        return <FulfillmentASNIndexPage />;
      case "transfers":
        return <InventoryTransferIndexPage />;
      case "fulfillment":
        return renderFulfillmentConfigs();
      case "warehouse":
        return renderWarehouseConfigs();
      case "carrier":
        return renderCarrierConfigs();
      case "integration":
        return renderIntegrationConfigs();
      case "permission":
        return <OrgPermissions permissions={org?.permissions || []} />;
      case "general":
      default:
        return <OrganizationDetailsCard organization={org} />;
    }
  };

  const renderOrgStatusDropdown = (status: Common.OrganizationStatus) => {
    const statuses = orgStatusTransition[status];
    return (
      <Dropdown
        disabled={org?.status === "churned"}
        overlay={
          <Menu>
            {statuses
              .filter((s) => s !== org?.status)
              .map((status) => (
                <Menu.Item key={status} onClick={() => setStatus(status)}>
                  {startCase(status)}
                </Menu.Item>
              ))}
          </Menu>
        }
      >
        <Tag color={orgStatusColor[status]}>{startCase(status)}</Tag>
      </Dropdown>
    );
  };
  return (
    <Card loading={orgQuery.isLoading}>
      <PageHeader
        title={org?.name}
        subTitle={
          <Space align="center">
            <Typography.Text type="secondary">Joined: {moment(org?.created_at).format("LL")}</Typography.Text>
            {renderOrgStatusDropdown(org?.status || "active")}
          </Space>
        }
        extra={[
          urlState.tab === "integration" ? (
            <Button key="api-partner" icon={<EditOutlined />} onClick={() => doCreateOrganizationApiPartner(orgId)}>
              Create/View API partner
            </Button>
          ) : null,
          <Button key="edit" onClick={() => history.push(`/organizations/${orgId}/edit`)} icon={<EditOutlined />}>
            Edit
          </Button>,
        ]}
        breadcrumb={
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to="/organizations">Organizations</Link>
            </Breadcrumb.Item>
            {!!org?.name && (
              <Breadcrumb.Item>
                <Link to={`/organizations/${orgId}`}>{org.name}</Link>
              </Breadcrumb.Item>
            )}
            {configurationTabs.includes(urlState.tab) ? (
              <>
                <Breadcrumb.Item>Configurations</Breadcrumb.Item>
                <Breadcrumb.Item>{getTabLabel(urlState.tab)}</Breadcrumb.Item>
              </>
            ) : (
              <Breadcrumb.Item>{getTabLabel(urlState.tab)}</Breadcrumb.Item>
            )}
          </Breadcrumb>
        }
      />
      <Flex direction="column" align="start" gap="1em">
        <span>
          <OrgMenu tab={urlState.tab} onChange={(tab) => handleTabChange(tab)} />
        </span>
        <span>{renderTabs(urlState.tab)}</span>
      </Flex>
      <ApiPartnerKeys
        visible={isVisible}
        updateModalState={() => setIsVisible(false)}
        clientId={clientId}
        clientSecret={clientSecret}
      />
      <Modal
        className="org-status-modal"
        visible={!!status && org?.status !== status}
        closable={false}
        title={`Are you sure you want to update ${org?.name}'s status to ${status}?`}
        onOk={() => {
          updateOrganization({ organizationID: orgId, body: { organization: { status } } });
        }}
        onCancel={() => {
          setStatus(org?.status);
        }}
        afterClose={() => {
          setIsConcentChecked(false);
        }}
        okText="Confirm"
        okButtonProps={{ disabled: !isConcentChecked || mutateOrg.isLoading, loading: mutateOrg.isLoading }}
        cancelButtonProps={{ disabled: mutateOrg.isLoading }}
      >
        <Space direction="vertical">
          {status === "churned" ? (
            <Typography.Text type="danger">
              You are about to set this merchant&apos;s status to &ldquo;churned&rdquo;. This action is permanent,
              cannot be undone, and will revoke all access for this merchant.
            </Typography.Text>
          ) : status === "paused" ? (
            <Typography.Text type="warning">
              You are about to set this merchant&apos;s status to &ldquo;paused&rdquo;. This action will temporarily
              suspend fulfillment operations for this merchant.
            </Typography.Text>
          ) : (
            <Typography.Text>
              You are about to set this merchant&apos;s status to &ldquo;active&rdquo;. This action will activate
              fulfillment operations for this merchant.
            </Typography.Text>
          )}
          <Checkbox onChange={(e) => setIsConcentChecked(e.target.checked)} checked={isConcentChecked}>
            I understand the consequences of this action.
          </Checkbox>
        </Space>
      </Modal>
    </Card>
  );
};

// new components
const configurationTabs = ["fulfillment", "warehouse", "carrier", "integration", "permission"];
const mainTabs = [
  "general",
  "users",
  "orders",
  "shipments",
  "returns",
  "products",
  "ASNs",
  "transfers",
  ["configurations", configurationTabs],
];

const tabLabelMap: Record<string, string> = {
  ASNs: "ASNs",
  warehouse: "Return Preferences",
  carrier: "Carrier Preferences",
};

export const getTabLabel = (key: string) => tabLabelMap[key] ?? startCase(key);

export const OrgMenu: React.FC<{ tab: string; onChange: (tab: string) => void }> = ({ tab, onChange }) => {
  const getLabel = (key: string) => getTabLabel(key);
  return (
    <Menu mode="horizontal" selectedKeys={[tab]} onClick={({ key }) => onChange(key)}>
      {mainTabs.map((item) => {
        if (typeof item !== "string") {
          const [label, values] = item as [string, string[]];
          return (
            <Menu.SubMenu key={label} title={getLabel(label)}>
              {values.map((item) => (
                <Menu.Item key={item}>{getLabel(item)}</Menu.Item>
              ))}
            </Menu.SubMenu>
          );
        }
        return <Menu.Item key={item}>{getLabel(item)}</Menu.Item>;
      })}
    </Menu>
  );
};

export const GeneralFulfillmentConfig: React.FC<{
  configs: Record<Common.OrganizationConfigKey, unknown>;
  setConfigs?: (configs: Record<Common.OrganizationConfigKey, unknown>) => unknown;
}> = ({ configs, setConfigs }) => {
  return (
    <Card title="Fulfillment">
      <List
        size="small"
        dataSource={Object.entries(generalFulfillmentConfig) || []}
        renderItem={([key, config]) => (
          <List.Item
            key={key}
            extra={
              <Switch
                onChange={(value) => setConfigs?.({ ...configs, [key]: value })}
                checked={["t", "true", true, "internal"].includes(
                  configs[key as unknown as Common.OrganizationConfigKey] as string
                )}
                disabled={!setConfigs || config?.readOnly}
              />
            }
          >
            {config.label}
          </List.Item>
        )}
      />
    </Card>
  );
};

export const OrgPermissions: React.FC<{ permissions: Common.Organization["permissions"] }> = ({ permissions }) => {
  return (
    <Card title="Permission">
      <List
        size="small"
        style={{ maxHeight: 350, overflowY: "auto" }}
        dataSource={permissions || []}
        renderItem={(item) => (
          <List.Item extra={<Switch defaultChecked disabled={true} />}>{startCase(item.name)}</List.Item>
        )}
      />
    </Card>
  );
};

export const Geofilter: React.FC<{ geofilter: Common.Organization["geofilter_constraints"] }> = ({ geofilter }) => {
  const geoFilterRegions = Object.values(location)
    .flat()
    .reduce((acc, cur) => ({ ...acc, [cur.region_code.toLowerCase()]: cur.region_name }), {} as Record<string, string>);

  const geoConstraints = geofilter?.map((c) => ({
    country: geoFilterConstraints[c.country],
    regions: c.provinces.map((p) => geoFilterRegions[p.province]),
  }));

  return (
    <Card title="Selected Fulfillable Area">
      <Typography.Text type="secondary">
        The selected regions bellow indicates that only orders with ship-to addresses within these regions should be
        synced into the GoBolt system. In the event that an order has a ship-to address that falls outside of the
        specified regions, it will be blocked from entering the GoBolt system.
      </Typography.Text>
      <Tabs>
        {!geoConstraints?.length ? (
          <Flex align="center" justify="center">
            <span>
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </span>
          </Flex>
        ) : (
          geoConstraints?.map((c) => (
            <Tabs.TabPane tab={c.country} key={c.country}>
              {c.regions.map((r) => (
                <Space key={r} style={{ margin: 2 }}>
                  <Tag>{r}</Tag>
                </Space>
              ))}
            </Tabs.TabPane>
          ))
        )}
      </Tabs>
    </Card>
  );
};
